/**
 * Revenue attribution utilities for affiliate click tracking.
 *
 * Provides:
 * - Unique click_id generation for linking clicks → conversions
 * - UTM parameter capture & persistence across the session
 * - Session ID management for grouping user activity
 * - Landing page tracking for first-touch attribution
 */

const SESSION_KEY = 'pb_session_id';
const UTM_KEY = 'pb_utm_params';
const LANDING_KEY = 'pb_landing_page';

// ─── Click ID ────────────────────────────────────────────────

/** Generate a unique click ID using crypto.randomUUID with timestamp prefix. */
export function generateClickId(): string {
  const ts = Date.now().toString(36);
  const rand = typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID().slice(0, 8)
    : Math.random().toString(36).slice(2, 10);
  return `${ts}-${rand}`;
}

// ─── Session tracking ────────────────────────────────────────

/** Get or create a persistent session ID (survives page navigations, clears on tab close). */
export function getSessionId(): string {
  let id = sessionStorage.getItem(SESSION_KEY);
  if (!id) {
    id = generateClickId();
    sessionStorage.setItem(SESSION_KEY, id);
  }
  return id;
}

// ─── UTM parameters ─────────────────────────────────────────

export interface UTMParams {
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_term: string | null;
  utm_content: string | null;
}

/**
 * Capture UTM parameters from the current URL and persist them in sessionStorage.
 * Called once on app mount — subsequent page navigations within the SPA preserve the values.
 * Only overwrites if the current URL actually contains UTM params (avoids clearing on internal nav).
 */
export function captureUTMParams(): UTMParams {
  const params = new URLSearchParams(window.location.search);
  const fromUrl: UTMParams = {
    utm_source: params.get('utm_source'),
    utm_medium: params.get('utm_medium'),
    utm_campaign: params.get('utm_campaign'),
    utm_term: params.get('utm_term'),
    utm_content: params.get('utm_content'),
  };

  // Only persist if the URL actually has at least one UTM param
  const hasUtm = Object.values(fromUrl).some(v => v != null);
  if (hasUtm) {
    sessionStorage.setItem(UTM_KEY, JSON.stringify(fromUrl));
  }

  return fromUrl;
}

/** Retrieve the stored UTM params, falling back to current URL query params. */
export function getUTMParams(): UTMParams {
  const stored = sessionStorage.getItem(UTM_KEY);
  if (stored) {
    try {
      return JSON.parse(stored) as UTMParams;
    } catch {
      // Corrupted — fall through
    }
  }

  // Fallback: read from current URL
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get('utm_source'),
    utm_medium: params.get('utm_medium'),
    utm_campaign: params.get('utm_campaign'),
    utm_term: params.get('utm_term'),
    utm_content: params.get('utm_content'),
  };
}

// ─── Landing page ────────────────────────────────────────────

/** Record the landing page URL on first visit (first-touch attribution). */
export function captureLandingPage(): string {
  let landing = sessionStorage.getItem(LANDING_KEY);
  if (!landing) {
    landing = window.location.href;
    sessionStorage.setItem(LANDING_KEY, landing);
  }
  return landing;
}

export function getLandingPage(): string | null {
  return sessionStorage.getItem(LANDING_KEY);
}

// ─── Click payload builder ───────────────────────────────────

export interface AffiliateClickPayload {
  click_id: string;
  session_id: string;
  firm_name: string;
  account_size: string;
  discount_code: string | null;
  affiliate_url: string;
  referrer_url: string;
  landing_page: string | null;
  source: string;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_term: string | null;
  utm_content: string | null;
}

export interface ClickParams {
  firmName: string;
  accountSize: string;
  discountCode: string | null;
  affiliateUrl: string;
  /** The component/page where the click originated, e.g. 'comparison_table', 'featured_firms' */
  source: string;
}

/** Build a complete click payload with all attribution data. */
export function buildClickPayload(params: ClickParams): AffiliateClickPayload {
  const utm = getUTMParams();
  return {
    click_id: generateClickId(),
    session_id: getSessionId(),
    firm_name: params.firmName,
    account_size: params.accountSize,
    discount_code: params.discountCode,
    affiliate_url: params.affiliateUrl,
    referrer_url: window.location.href,
    landing_page: getLandingPage(),
    source: params.source,
    utm_source: utm.utm_source,
    utm_medium: utm.utm_medium,
    utm_campaign: utm.utm_campaign,
    utm_term: utm.utm_term,
    utm_content: utm.utm_content,
  };
}
